import { ISelectedCloningSession } from "../controllers/clone-sessions";
import { IdMap } from "@gtmhub/util/utils";
import { ITask } from "@gtmhub/tasks";
import { IGoal } from "@gtmhub/goals/models";
import { ICollection, RequireAtLeastOne } from "@gtmhub/models";
import { ITag } from "@gtmhub/tags/models";
import { IMetric } from "@gtmhub/okrs/metrics";
import { DynamicValueType } from "../../lists/models/models";
import { IGoalCloneSelected, MethodType } from "@gtmhub/goals/controllers/clone-or-move-goal";
import { AssignPermissions } from "@gtmhub/configuration";

export type CloningWorkerStatus = "cloning" | "done" | "starting" | "error" | "conflicts" | "continue" | "rollback_done";
export type CadenceType = "WEEKLY" | "BI-WEEKLY" | "MONTHLY";
export type CloneOrMoveGoalWorkerStatus = "in_progress" | "cloning" | "goal_moved" | "done" | "starting" | "error" | "continue" | "conflicts" | "rollback";
type Weekdays = "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday";
type CadenceMonthPeriod = "firstDay" | "lastDay" | "specificDay";

export interface ICloningWorkerData {
  params?: ICloningWorkerDataParams;
  headers?: Record<string, string>;
  paths?: ICloningWorkerDataPaths;
  info?: {
    selectedSession: ISelectedCloningSession;
    selectedSessionParentId: string;
    currentUserId?: string;
  };
  counter?: number;
  totalItemsCounter?: number;
  currentProgress?: number;
  status?: CloningWorkerStatus;
  goal?: IGoal;
  session?: IPlanningSession;
  rollback?: boolean;
  conflicts?: ICollection<IPlanningSessionConflict>;
}

export interface ICloningWorkerDataPaths {
  goals: string;
  metrics: string;
  tasks: string;
  tasksV2: string;
  session: string;
  links: string;
  customFieldsV1: string;
  customFieldsV2: string;
}

export interface ICloningWorkerDataParams {
  includeMetrics: boolean;
  includeTasks: boolean;
  sessionId: string;
  fields?: string;
}

export interface ICloneOrMoveGoalWorkerData {
  headers?: Record<string, string>;
  paths?: ICloneOrMoveGoalWorkerPaths;
  info?: {
    token: string;
    accountId: string;
    currentUserId: string;
    goal?: IGoal;
    method?: string;
    selected?: IGoalCloneSelected;
    parentChildMap?: { [id: string]: IGoal[] };
  };
  counter?: number;
  totalItemsCounter?: number;
  currentProgress?: number;
  status?: CloneOrMoveGoalWorkerStatus;
  action?: MethodType;
  goal?: IGoal;
  goalId?: string;
  session?: IPlanningSession;
  rollback?: boolean;
  conflicts?: ICollection<IPlanningSessionConflict>;
}
export interface ICloneOrMoveGoalWorkerPaths {
  goals: string;
  goalsV2: string;
  metrics: string;
  metricsV2: string;
  tasks: string;
  tasksV2: string;
  session: string;
  links: string;
  insights: string;
}

export interface IStratifiedPlanningSession {
  data: IPlanningSession;
  id: string;
  title?: string;
  parent?: IPlanningSession;
}

export interface ICloningSession {
  resetProgress?: boolean;
  sendNotifications?: boolean;
  cloneTasks?: boolean;
  cloneCustomFields?: boolean;
  cloneOnlyParentObjectives?: boolean;
  sessionId: string;
  sessionParentId: string;
  title: string;
  shouldAlignSession?: boolean;
}

export interface ICadenceStart {
  weekday?: Weekdays;
  dayOfMonth?: number;
  periodOfMonth?: CadenceMonthPeriod;
}

export interface IPlanningSession {
  accountId?: string;
  end?: string;
  id?: string;
  start?: string;
  title?: string;
  ui?: any;
  status?: PlanningSessionStatusType;
  demo?: boolean;
  timePassedPercentage?: any; // only used in the web app
  access?: IAccess;
  color?: string;
  cadence?: IPlanningSessionCadence;
  parentId?: string;
  cadenceType?: CadenceType;
  cadenceStart?: RequireAtLeastOne<ICadenceStart>;

  workflow?: IPlanningSessionWorkflow;

  daysLength?: number;
  lengthPercentage?: number;
  leftPercentage?: number;
  selected?: boolean;
  tags?: ITag[];
  cadenceTimeZone?: string;
  userHasPermissionToCreate?: boolean;
  settings?: ISessionSettings;
  isSelfApproveAllowed?: boolean;
}

export interface ISessionSettings {
  assignGoalPermissions: AssignPermissions;
  assignMetricPermissions: AssignPermissions;
  multipleOwners: {
    disabledForMetrics: boolean;
    disabledForGoals: boolean;
  };
  progressAggregationDisabled?: boolean;
  goalsPerOwnerLimit?: number;
  metricsPerGoalLimit?: number;
}

export interface IPlanningSessionFilterView extends IPlanningSession {
  dynamicValueType?: DynamicValueType;
}

export type PlanningSessionStatusType = "open" | "closed" | "locked";

export type IPrincipalKind = "team" | "user" | "role" | "account" | "team_and_subteams";

export enum PlanningSessionStatus {
  OPEN = "open",
  ARCHIVED = "closed",
  INPROGRESS = "locked",
}

export interface IPlanningSessionWorkflow {
  reviewers: number;
  target: string[];
  editableOkrFields?: string[];
  editableKrFields?: string[];
}

export interface IPlanningSessionParams {
  fields?: string;
  status?: string;
  includeDemo?: boolean;
}

export type ISessionsIdMap = IdMap<IPlanningSession>;

export type ISessionsIdParentMap = IdMap<{ id: string; parentId?: string }>;

export interface IPlanningSessionFilter {
  id?: string;
}

export interface IPlanningSessionStats {
  goalCount: number;
  metricsCount: string;
  progress: number;
  daysToDeadline: string;
  dynamicMetricCount: string;
  manualMetricCount: string;
  goalsMetCount: string;
  goodMetricsCount: string;
  badMetricsCount: string;
  metricsMetCount: string;
}

export interface IAccess {
  inherits?: boolean;
  permissions?: IApiPermission[];
}

export interface IApiPermission {
  grant: {
    general: (keyof IAllowedActionsMap)[];
  };
  principalId: string;
  principalKind: IPrincipalKind;
  includeSubteams?: boolean;
}

export interface IPlanningSessionConflict {
  severity: string;
  type: string;
  goals: ICollection<IGoal>;
  metrics: ICollection<IMetric>;
  sessions: ICollection<IPlanningSession>;
}

export interface IMetricConflicts {
  [type: string]: {
    type: string;
    metrics: IMetric[];
  };
}

export interface IPlanningSessionCadence {
  type: CadenceType;
  totalCadences?: number;
  ranges?: IPlanningSessionCadenceRange[];
}

export interface IPlanningSessionCadenceRange {
  cadenceOrder: number;
  end: string;
  start: string;
}

export interface IAssigneeItemsByCadence {
  cadence: IPlanningSessionCadence;
  session: IPlanningSession;
  goals: IGoal[];
  metrics: IMetric[];
  tasks: ITask[];
}

export interface IPlanningSessionsIdMap {
  [id: string]: IPlanningSession;
}

export interface ISessionWorkflowStats {
  approvedGoalsPercentage: number;
  approvedGoalsCount: number;
  pendingGoals: number;
  draftGoals: number;
  topLevelGoalsPercentage: number;
  topLevelGoalsCount: number;
  crossSessionAligned: number;
  pendingReviewers: number;
  draftOwners: number;
  averageApprovalPeriod: number;
}

export interface IUserItemAllowedActions {
  currentUserActions: UserAction[];
}

export type UserAction = keyof IAllowedActionsMap;

export interface IAllowedActionsMap {
  read?: boolean;
  update?: boolean;
  create?: boolean;
  delete?: boolean;
  modifyPermissions?: boolean;
}

export interface ISessionFilterView {
  id: string;
  title: string;
}

export interface IPlanningSessionStatsAssignee {
  assigneeId: string;
  planningSessionStats: IPlanningSessionStats;
}

export interface IWorkflowSelectionField {
  name: string;
  label: string;
  checked?: boolean;
}
